import type { DiscoverComment } from '@/types';

/**
 * G-tr (2.1.0) — comments are translated at creation (backend) and displayed
 * in the reader's language; "see original" lives in the card's "…" menu.
 * Pure so the language-pick rules are unit-testable.
 */
export function pickCommentDisplay(
  comment: Pick<DiscoverComment, 'content' | 'translations'>,
  language: string,
): { text: string; isTranslated: boolean } {
  // Indexed by the reader's locale without narrowing to fr|en: if the backend
  // ever stores another locale it is picked up here rather than silently
  // falling back to the original (`source` is metadata, never a translation).
  const translations = comment.translations as
    Record<string, string | undefined> | null | undefined;
  const translated = language !== 'source' ? translations?.[language] : undefined;
  if (typeof translated === 'string' && translated.length > 0 && translated !== comment.content) {
    return { text: translated, isTranslated: true };
  }
  return { text: comment.content, isTranslated: false };
}

/**
 * reco-comment-replies — the author's legend among a reco's comments.
 *
 * The legend is the one with NO circle: it is the same in every circle the reco
 * was shared to, while a reply is scoped to one. Callers used `comments[0]`,
 * which was a bet on insertion order — correct only while replies did not exist.
 * A comment with no `group` key at all (payload from before the field shipped)
 * counts as a legend.
 */
export function pickCaption<T extends Pick<DiscoverComment, 'id' | 'group'>>(
  comments: T[] | undefined | null,
): T | undefined {
  return comments?.find((c) => c.group == null);
}

/**
 * reco-sheet-per-gesture — the discussion's legend follows the card's legend.
 *
 * The thread is loaded once into the card's local state and carries the legend
 * as its first message (`group == null`). When the author edits the legend, the
 * feed cache is rewritten, not that state: reopening the discussion showed the
 * old text under a card already showing the new one. The card's legend wins on
 * text and translations; identity, author and position stay the thread's.
 */
export function syncCaption<T extends { content: string; group?: { id: number } | null }>(
  thread: T[] | null,
  caption: (Pick<DiscoverComment, 'content'> & Partial<DiscoverComment>) | undefined,
): T[] | null {
  if (!thread || !caption) return thread;
  const i = thread.findIndex((c) => c.group == null);
  if (i < 0) return thread;
  const legend = thread[i] as T & { translations?: unknown };
  if (legend.content === caption.content && legend.translations === caption.translations) {
    return thread;
  }
  const next = [...thread];
  next[i] = { ...legend, content: caption.content, translations: caption.translations ?? null };
  return next;
}
